import { createSignal, onMount, Show } from "solid-js";
import Text from "./Text";
import InstallAppButton from "./InstallAppButton";
import { useToast } from "./MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import { useApi, isOk } from "../api";
import {
  isIos,
  isStandalone,
  pushSupported,
  withTimeout,
  getReadyRegistration,
  pushSubscribed,
  setPushSubscribed,
  refreshPushSubscribed,
} from "../services/pwa";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function errDetail(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

export default function PushNotificationToggle() {
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation();

  const supported = pushSupported();
  const iosNeedsInstall = supported && isIos() && !isStandalone();

  const [busy, setBusy] = createSignal(false);

  onMount(() => {
    if (supported && !iosNeedsInstall) void refreshPushSubscribed();
  });

  async function enable() {
    if (busy()) return;
    setBusy(true);
    try {
      if (Notification.permission === "denied") {
        toast?.warning(t("NOTIFICATIONS_PERMISSION_BLOCKED"));
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast?.warning(t("NOTIFICATIONS_PERMISSION_DENIED"));
        return;
      }

      const reg = await getReadyRegistration();

      const configRes = await withTimeout(api.push.getConfig(), 10000, "config");
      if (!isOk(configRes.data) || !configRes.data.value?.vapidPublicKey) {
        toast?.error(t("NOTIFICATIONS_ERROR"));
        return;
      }

      const sub = await withTimeout(reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(configRes.data.value.vapidPublicKey),
      }), 15000, "subscribe");

      const json = sub.toJSON();
      const res = await api.push.subscribe({
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? "",
        auth: json.keys?.auth ?? "",
        userAgent: navigator.userAgent,
      });

      if (!isOk(res.data)) {
        toast?.error(t("NOTIFICATIONS_ERROR"));
        return;
      }
      setPushSubscribed(true);
      toast?.success(t("NOTIFICATIONS_ENABLED_TOAST"));
    } catch (e) {
      toast?.error(`${t("NOTIFICATIONS_ERROR")} (${errDetail(e)})`);
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (busy()) return;
    setBusy(true);
    try {
      const reg = await getReadyRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.push.unsubscribe({ endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushSubscribed(false);
      toast?.info(t("NOTIFICATIONS_DISABLED_TOAST"));
    } catch (e) {
      toast?.error(`${t("NOTIFICATIONS_ERROR")} (${errDetail(e)})`);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    if (busy()) return;
    setBusy(true);
    try {
      const res = await api.push.sendTest();
      if (!isOk(res.data)) {
        toast?.error(t("NOTIFICATIONS_ERROR"));
        return;
      }
      const sent = res.data.value?.sent ?? 0;
      if (sent > 0) toast?.success(t("NOTIFICATIONS_TEST_SENT", sent));
      else toast?.warning(t("NOTIFICATIONS_TEST_NONE"));
    } catch {
      toast?.error(t("NOTIFICATIONS_ERROR"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="v" style={{ gap: "var(--spacing-md)", "max-width": "520px" }}>
      <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}>
        <Text val="NOTIFICATIONS_DESC" />
      </span>

      <Show when={!supported}>
        <div class="fcard hcenter" style={{ gap: "var(--spacing-sm)" }}>
          <i class="fa-solid fa-bell-slash" style={{ opacity: 0.6 }} />
          <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}>
            <Text val="NOTIFICATIONS_UNSUPPORTED" />
          </span>
        </div>
      </Show>

      <Show when={iosNeedsInstall}>
        <div class="v fcard" style={{ gap: "var(--spacing-md)" }}>
          <div class="hcenter" style={{ gap: "var(--spacing-sm)" }}>
            <i class="fa-solid fa-arrow-up-from-bracket" style={{ opacity: 0.6 }} />
            <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}>
              <Text val="NOTIFICATIONS_IOS_INSTALL" />
            </span>
          </div>
          <InstallAppButton />
        </div>
      </Show>

      <Show when={supported && !iosNeedsInstall}>
        <div class="h" style={{ gap: "var(--spacing-sm)", "flex-wrap": "wrap" }}>
          <Show
            when={pushSubscribed()}
            fallback={
              <button class="btn btn-primary" onClick={enable} disabled={busy()}>
                <Show when={busy()} fallback={<><i class="fa-solid fa-bell" /> <Text val="NOTIFICATIONS_ENABLE" /></>}>
                  <i class="fa-solid fa-spinner fa-spin" />
                </Show>
              </button>
            }
          >
            <button class="btn btn-secondary" onClick={sendTest} disabled={busy()}>
              <i class="fa-solid fa-paper-plane" /> <Text val="NOTIFICATIONS_SEND_TEST" />
            </button>
            <button class="btn btn-danger" onClick={disable} disabled={busy()}>
              <i class="fa-solid fa-bell-slash" /> <Text val="NOTIFICATIONS_DISABLE" />
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}
